import { type FormEvent, useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import type { ConversationResponse, DocumentResponse } from "@bubble/core";
import { askPreview } from "../components/ChatBubble.tsx";
import { SiteStage } from "../components/SiteStage.tsx";
import { Steps } from "../components/Steps.tsx";
import { api, messageOf, type AgentResponse } from "../lib/api.ts";
import { useAuth, useToken } from "../lib/auth.tsx";
import { markStep, nextLabel, nextStep, previewNote, progressFor, readMarks, SAMPLE_QUESTIONS, stepPath, type Progress } from "../lib/setup.ts";

export function Home() {
  const { me, meError, refreshMe } = useAuth();
  const token = useToken();
  if (meError) {
    return (
      <section className="card narrow">
        <p className="banner bad">{meError}</p>
        <button className="button" type="button" onClick={() => void refreshMe()}>
          Try again
        </button>
      </section>
    );
  }
  if (!me) return <p className="muted">Loading…</p>;
  if (!me.tenant) return <Welcome token={token} hasTenant={false} onTenant={() => void refreshMe()} onReady={() => undefined} />;
  return <Overview token={token} />;
}

function Welcome({
  token,
  hasTenant,
  onTenant,
  onReady,
}: {
  token: string;
  hasTenant: boolean;
  onTenant: () => void;
  onReady: (agent: AgentResponse) => void;
}) {
  const navigate = useNavigate();
  const [businessName, setBusinessName] = useState("");
  const [agentName, setAgentName] = useState("");
  const [own, setOwn] = useState(false);
  const [error, setError] = useState("");
  const [pending, setPending] = useState<"sample" | "own" | "">("");

  async function openSample() {
    setPending("sample");
    setError("");
    try {
      const name = businessName.trim();
      const body = await api<{ agent: AgentResponse }>("/v1/sample-agent", token, {
        method: "POST",
        json: name ? { businessName: name } : {},
      });
      if (!hasTenant) onTenant();
      else onReady(body.agent);
    } catch (caught) {
      setError(messageOf(caught));
      setPending("");
    }
  }

  async function createOwn(event: FormEvent) {
    event.preventDefault();
    setPending("own");
    setError("");
    try {
      if (!hasTenant) {
        await api("/v1/tenants", token, { method: "POST", json: { name: businessName.trim() } });
        onTenant();
      }
      const body = await api<{ agent: AgentResponse }>("/v1/agents", token, { method: "POST", json: { name: agentName.trim() } });
      navigate(`/app/agents/${body.agent.id}/knowledge`);
    } catch (caught) {
      setError(messageOf(caught));
      setPending("");
    }
  }

  return (
    <section className="card narrow stack">
      <p className="eyebrow">Start here</p>
      <h1>See an agent answer before you build yours</h1>
      <p className="lead">
        Harbor & Rye is a sample cafe with its notes already loaded. Chat with it, then make an agent that only answers from your files.
      </p>
      {!hasTenant ? (
        <label>
          Your business name
          <input
            value={businessName}
            minLength={2}
            maxLength={120}
            placeholder="My business"
            required={own}
            onChange={(event) => setBusinessName(event.target.value)}
          />
        </label>
      ) : null}
      {own ? (
        <form className="stack" onSubmit={createOwn}>
          <label>
            Agent name
            <input required maxLength={80} placeholder="Front desk" value={agentName} onChange={(event) => setAgentName(event.target.value)} />
          </label>
          <p className="muted">This is the name you see in the dashboard. Visitors can see a different name.</p>
          {error ? <p className="banner bad">{error}</p> : null}
          <button className="button block" type="submit" disabled={pending !== ""}>
            {pending === "own" ? "Creating…" : "Create your agent"}
          </button>
          <button className="text-link" type="button" onClick={() => setOwn(false)}>
            Back to the sample
          </button>
        </form>
      ) : (
        <>
          {error ? <p className="banner bad">{error}</p> : null}
          <button className="button block" type="button" disabled={pending !== ""} onClick={() => void openSample()}>
            {pending === "sample" ? "Getting the cafe ready…" : "Chat with the sample"}
          </button>
          <button className="text-link" type="button" onClick={() => setOwn(true)}>
            I want to set up my own
          </button>
        </>
      )}
    </section>
  );
}

function Overview({ token }: { token: string }) {
  const [agents, setAgents] = useState<AgentResponse[] | null>(null);
  const [error, setError] = useState("");

  useEffect(() => {
    let active = true;
    void api<{ agents: AgentResponse[] }>("/v1/agents", token)
      .then((body) => {
        if (active) setAgents(body.agents);
      })
      .catch((caught: unknown) => {
        if (active) setError(messageOf(caught));
      });
    return () => {
      active = false;
    };
  }, [token]);

  if (error) return <p className="banner bad">{error}</p>;
  if (!agents) return <p className="muted">Loading your agents…</p>;
  const own = agents.filter((agent) => !agent.isSample);
  const sample = agents.find((agent) => agent.isSample) ?? null;
  if (own.length === 0 && !sample) {
    return (
      <Welcome
        token={token}
        hasTenant
        onTenant={() => undefined}
        onReady={(agent) => setAgents((current) => [...(current ?? []).filter((item) => item.id !== agent.id), agent])}
      />
    );
  }
  if (own.length === 0 && sample) return <SampleHome agent={sample} token={token} />;
  return <OwnHome agents={own} sample={sample} token={token} />;
}

function SampleHome({ agent, token }: { agent: AgentResponse; token: string }) {
  const [own, setOwn] = useState(false);
  return (
    <div className="sample-layout">
      <div className="sample-preview stack">
        <div className="suggestions">
          {SAMPLE_QUESTIONS.map((question) => (
            <button key={question} className="chip" type="button" onClick={() => askPreview(agent.id, question)}>
              {question}
            </button>
          ))}
        </div>
        <SiteStage agentId={agent.id} name={agent.name} theme={agent.theme} sample note={previewNote(agent, false)} onAnswered={() => markStep(agent.id, "test")} />
      </div>
      <div className="sample-copy stack">
        <p className="banner">This is a sample cafe, not your business. Ask it something, then start your own agent.</p>
        <header className="step-head">
          <p className="eyebrow">Sample</p>
          <h1>Harbor & Rye</h1>
          <p className="lead">The bubble is the real chat. It answers from the cafe’s notes and shows its sources.</p>
        </header>
        {own ? (
          <CreateAgent token={token} />
        ) : (
          <button className="button block" type="button" onClick={() => setOwn(true)}>
            Start your own agent
          </button>
        )}
        <Link className="text-link" to={`/app/agents/${agent.id}/knowledge`}>
          Look through the sample setup
        </Link>
      </div>
    </div>
  );
}

function OwnHome({ agents, sample, token }: { agents: AgentResponse[]; sample: AgentResponse | null; token: string }) {
  const [featuredId, setFeaturedId] = useState(agents[0]?.id ?? "");
  const [creating, setCreating] = useState(false);
  const [samplePending, setSamplePending] = useState(false);
  const [sampleError, setSampleError] = useState("");
  const navigate = useNavigate();
  const featured = agents.find((agent) => agent.id === featuredId) ?? agents[0];
  if (!featured) return null;

  async function openSample() {
    setSamplePending(true);
    setSampleError("");
    try {
      const body = await api<{ agent: AgentResponse }>("/v1/sample-agent", token, { method: "POST", json: {} });
      navigate(`/app/agents/${body.agent.id}/test`);
    } catch (caught) {
      setSampleError(messageOf(caught));
      setSamplePending(false);
    }
  }

  return (
    <div className="stack">
      <header className="step-head">
        <p className="eyebrow">Your agent</p>
        <h1>{featured.name}</h1>
        <p className="lead">Pick up the next step. The preview shows the chat your visitors will get.</p>
      </header>
      <Featured agent={featured} />
      {agents.length > 1 ? (
        <div className="suggestions" role="group" aria-label="Your agents">
          {agents.map((agent) => (
            <button key={agent.id} className={agent.id === featured.id ? "chip on" : "chip"} type="button" onClick={() => setFeaturedId(agent.id)}>
              {agent.name}
            </button>
          ))}
        </div>
      ) : null}
      {creating ? (
        <CreateAgent token={token} />
      ) : (
        <button className="text-link" type="button" onClick={() => setCreating(true)}>
          Create another agent
        </button>
      )}
      {sample ? (
        <Link className="text-link" to={`/app/agents/${sample.id}/test`}>
          Open the sample cafe
        </Link>
      ) : (
        <button className="text-link" type="button" disabled={samplePending} onClick={() => void openSample()}>
          {samplePending ? "Getting the cafe ready…" : "Open the sample cafe"}
        </button>
      )}
      {sampleError ? <p className="banner bad">{sampleError}</p> : null}
    </div>
  );
}

function Featured({ agent }: { agent: AgentResponse }) {
  const token = useToken();
  const [progress, setProgress] = useState<Progress | null>(null);

  useEffect(() => {
    let active = true;
    void Promise.all([
      api<{ documents: DocumentResponse[] }>(`/v1/agents/${agent.id}/documents`, token),
      api<{ conversations: ConversationResponse[] }>(`/v1/agents/${agent.id}/conversations`, token),
    ])
      .then(([documents, conversations]) => {
        if (!active) return;
        setProgress(
          progressFor({
            agent,
            documents: documents.documents,
            conversations: conversations.conversations,
            marked: readMarks(agent.id),
          }),
        );
      })
      .catch(() => {
        if (active) setProgress(null);
      });
    return () => {
      active = false;
    };
  }, [agent, token]);

  const step = progress ? nextStep(progress) : "knowledge";
  return (
    <div className="home-grid">
      <div className="home-preview">
      <SiteStage
        agentId={agent.id}
        name={agent.name}
        theme={agent.theme}
        sample={false}
        note={previewNote(agent, false)}
        onAnswered={() => markStep(agent.id, "test")}
      />
      </div>
      <div className="home-copy stack">
        {progress ? <Steps agentId={agent.id} progress={progress} current={step} /> : <p className="muted">Checking your progress…</p>}
        <Link className="button block" to={stepPath(agent.id, step)}>
          {progress ? nextLabel(progress) : "Continue"}
        </Link>
      </div>
    </div>
  );
}

function CreateAgent({ token }: { token: string }) {
  const navigate = useNavigate();
  const [name, setName] = useState("");
  const [error, setError] = useState("");
  const [pending, setPending] = useState(false);

  async function onSubmit(event: FormEvent) {
    event.preventDefault();
    setPending(true);
    setError("");
    try {
      const body = await api<{ agent: AgentResponse }>("/v1/agents", token, { method: "POST", json: { name: name.trim() } });
      navigate(`/app/agents/${body.agent.id}/knowledge`);
    } catch (caught) {
      setError(messageOf(caught));
      setPending(false);
    }
  }

  return (
    <form className="card stack" onSubmit={onSubmit}>
      <label>
        Agent name
        <input required maxLength={80} placeholder="Front desk" value={name} onChange={(event) => setName(event.target.value)} />
      </label>
      <p className="muted">You can change the name visitors see later.</p>
      {error ? <p className="banner bad">{error}</p> : null}
      <button className="button block" type="submit" disabled={pending}>
        {pending ? "Creating…" : "Create your agent"}
      </button>
    </form>
  );
}
